// ============================================
// NW Messenger (app.northworld.ai)
//
// Telegram-style chat + Web Push notifications
// ============================================
import { BrowserRouter, Routes, Route, Navigate, Outlet, useLocation } from 'react-router'
import { Suspense, lazy, useEffect } from 'react'
import { useAuth } from '@/shared/hooks/useAuth'
import { ErrorBoundary } from '@/shared/components/ErrorBoundary'

// Eagerly loaded
import { Login } from '@/routes/Login'

// Lazy load
const ChatList = lazy(() => import('@/routes/ChatList').then((m) => ({ default: m.ChatList })))
const Chat = lazy(() => import('@/routes/Chat').then((m) => ({ default: m.Chat })))
const Settings = lazy(() => import('@/routes/Settings').then((m) => ({ default: m.Settings })))

// ── Helpers ────────────────────────────────────────────────────────────────

function PageLoader() {
  return (
    <div
      className="flex-1 flex items-center justify-center"
      style={{ backgroundColor: 'var(--tg-bg)' }}
    >
      <div
        className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin"
        style={{ borderColor: 'var(--tg-accent)', borderTopColor: 'transparent' }}
      />
    </div>
  )
}

// Auth guard
function RequireAuth() {
  const { isAuthenticated, isLoading } = useAuth()
  if (isLoading) return <PageLoader />
  if (!isAuthenticated) return <Navigate to="/login" replace />
  return <Outlet />
}

// ── Page transition wrapper ──────────────────────────────────────────────
function AnimatedPage({ children, slide = false }: { children: React.ReactNode; slide?: boolean }) {
  const location = useLocation()
  return (
    <div
      key={location.pathname}
      className={slide ? 'page-slide-in' : 'page-fade-in'}
      style={{ display: 'flex', flex: 1, flexDirection: 'column', minHeight: 0 }}
    >
      {children}
    </div>
  )
}

// Messenger shell
function MessengerShell() {
  const location = useLocation()
  const isChatScreen = location.pathname.startsWith('/chat/')

  // Keyboard-resize: VisualViewport API
  useEffect(() => {
    const vv = window.visualViewport
    if (!vv) return
    const onResize = () => {
      const offsetBottom = window.innerHeight - vv.height - vv.offsetTop
      document.documentElement.style.setProperty('--keyboard-height', `${Math.max(0, offsetBottom)}px`)
    }
    vv.addEventListener('resize', onResize)
    vv.addEventListener('scroll', onResize)
    return () => {
      vv.removeEventListener('resize', onResize)
      vv.removeEventListener('scroll', onResize)
    }
  }, [])

  return (
    <div
      className="flex flex-col no-select app-container"
      style={{ height: '100dvh', backgroundColor: 'var(--tg-bg)', color: 'var(--tg-text)' }}
    >
      <AnimatedPage slide={isChatScreen}>
        <Outlet />
      </AnimatedPage>
    </div>
  )
}

// ── Routes ────────────────────────────────────────────────────────────────

export function App() {
  useEffect(() => {
    document.title = 'NW Messenger'
  }, [])

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <Suspense fallback={<PageLoader />}>
          <Routes>
            <Route path="/login" element={<Login />} />

            <Route element={<RequireAuth />}>
              <Route element={<MessengerShell />}>
                <Route path="/" element={<ChatList />} />
                <Route path="/chats" element={<ChatList />} />
                <Route path="/chat/:charId" element={<Chat />} />
                <Route path="/settings" element={<Settings />} />
              </Route>
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
    </ErrorBoundary>
  )
}
